<template>
  <div class="admin-overview">
    

    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner label="Loading..." />
    </div>

    <b-alert
      v-else-if="loadError"
      show
      variant="danger"
    >
      {{ loadError }}
    </b-alert>

    <b-row
      v-else
      class="match-height"
    >
      <!-- Orders -->
      <b-col
        cols="12"
        md="6"
        class="mb-2"
      >
        <b-card class="overview-card h-100">
          <div class="d-flex justify-content-between align-items-start mb-1">
            <div class="d-flex align-items-start">
              <b-avatar
                size="42"
                rounded
                variant="light-primary"
                class="mr-1 flex-shrink-0"
              >
                <feather-icon
                  :icon="ordersIcon"
                  size="18"
                />
              </b-avatar>
              <div>
                <h4 class="mb-25">
                  {{ ordersTitle }}
                </h4>
                <p class="text-muted mb-0 small">
                  {{ ordersSubtitle }}
                </p>
              </div>
            </div>
            <b-badge
              :variant="orders.new_count > 0 ? 'danger' : 'secondary'"
              class="overview-count"
            >
              {{ orders.new_count }}
            </b-badge>
          </div>

          <div
            v-if="orders.items.length === 0"
            class="text-muted py-1"
          >
            {{ isOwnerView ? 'No orders in the last 24 hours.' : 'No unassigned new orders.' }}
          </div>

          <ul
            v-else
            class="overview-list list-unstyled mb-1"
          >
            <li
              v-for="order in orders.items"
              :key="order.id"
              class="overview-list-item"
            >
              <div class="d-flex justify-content-between">
                <span>
                  <strong>#{{ order.id }}</strong>
                  <span
                    v-if="order.customer_name"
                    class="text-muted"
                  > — {{ order.customer_name }}</span>
                </span>
                <span class="text-nowrap text-muted small ml-1">
                  {{ formatDateTime(order.created_at) }}
                </span>
              </div>
              <div class="small text-muted">
                {{ statusName(order.status) }}
                <span v-if="order.total != null"> · {{ formatMoney(order.total) }}</span>
              </div>
            </li>
          </ul>

          <router-link
            :to="{ name: ordersRouteName }"
            class="font-weight-bold"
          >
            View all {{ ordersLabel.toLowerCase() }}
          </router-link>
        </b-card>
      </b-col>

      <!-- Waiting staff (company owner only) -->
      <b-col
        v-if="isOwnerView"
        cols="12"
        md="6"
        class="mb-2"
      >
        <b-card class="overview-card h-100">
          <div class="d-flex align-items-start mb-1">
            <b-avatar
              size="42"
              rounded
              variant="light-info"
              class="mr-1 flex-shrink-0"
            >
              <feather-icon
                icon="UserPlusIcon"
                size="18"
              />
            </b-avatar>
            <div>
              <h4 class="mb-25">
                Waiting staff members
              </h4>
              <p class="text-muted mb-0 small">
                Team members pending first login
              </p>
            </div>
          </div>

          <template v-if="!waitingStaff.has_staff">
            <p class="mb-1">
              Invite your team member to the board
            </p>
            <router-link
              :to="{ name: 'staff' }"
              class="font-weight-bold"
            >
              Go to staff
            </router-link>
          </template>

          <template v-else-if="waitingStaff.items.length === 0">
            <p class="text-muted mb-1">
              All staff members have activated their accounts.
            </p>
            <router-link
              :to="{ name: 'staff' }"
              class="font-weight-bold"
            >
              Manage staff
            </router-link>
          </template>

          <template v-else>
            <p class="text-muted small mb-1">
              Created but have not logged in and changed their password yet.
            </p>
            <ul class="overview-list list-unstyled mb-1">
              <li
                v-for="member in waitingStaff.items"
                :key="member.id"
                class="overview-list-item"
              >
                <strong>{{ member.name || '—' }}</strong>
                <div class="small text-muted">
                  {{ member.email }}
                </div>
              </li>
            </ul>
            <router-link
              :to="{ name: 'staff' }"
              class="font-weight-bold"
            >
              Manage staff
            </router-link>
          </template>
        </b-card>
      </b-col>

      <!-- Low stock / expiring products -->
      <b-col
        cols="12"
        md="6"
        class="mb-2"
      >
          <b-card class="overview-card h-100">
            <div class="d-flex justify-content-between align-items-start mb-1">
              <div class="d-flex align-items-start">
                <b-avatar
                  size="42"
                  rounded
                  variant="light-warning"
                  class="mr-1 flex-shrink-0"
                >
                  <feather-icon
                    icon="AlertTriangleIcon"
                    size="18"
                  />
                </b-avatar>
                <div>
                  <h4 class="mb-25">
                    Expiring products
                  </h4>
                  <p class="text-muted mb-0 small">
                    Stock management enabled, quantity below 5
                  </p>
                </div>
              </div>
              <b-badge
                :variant="lowStockProducts.length > 0 ? 'warning' : 'secondary'"
                class="overview-count"
              >
                {{ lowStockProducts.length }}
              </b-badge>
            </div>

            <div
              v-if="lowStockProducts.length === 0"
              class="text-muted py-1"
            >
              No low-stock products.
            </div>

            <ul
              v-else
              class="overview-list list-unstyled mb-1"
            >
              <li
                v-for="product in lowStockProducts"
                :key="product.id"
                class="overview-list-item d-flex justify-content-between"
              >
                <span>
                  <strong>{{ product.name }}</strong>
                  <span
                    v-if="product.sku"
                    class="text-muted small"
                  > ({{ product.sku }})</span>
                </span>
                <span
                  class="font-weight-bold ml-1"
                  :class="product.total_stock < 0 ? 'text-danger' : 'text-warning'"
                >
                  {{ product.total_stock }}
                </span>
              </li>
            </ul>

            <router-link
              :to="{ name: productsRouteName }"
              class="font-weight-bold"
            >
              View {{ productsLabel.toLowerCase() }}
            </router-link>
          </b-card>
      </b-col>

      <!-- Last customers -->
      <b-col
        cols="12"
        md="6"
        class="mb-2"
      >
        <b-card class="overview-card h-100">
          <div class="d-flex align-items-start mb-1">
            <b-avatar
              size="42"
              rounded
              variant="light-success"
              class="mr-1 flex-shrink-0"
            >
              <feather-icon
                icon="UsersIcon"
                size="18"
              />
            </b-avatar>
            <div>
              <h4 class="mb-25">
                Last customers
              </h4>
              <p class="text-muted mb-0 small">
                Most recently created customers
              </p>
            </div>
          </div>

          <div
            v-if="recentCustomers.length === 0"
            class="text-muted py-1"
          >
            No customers yet.
          </div>

          <ul
            v-else
            class="overview-list list-unstyled mb-1"
          >
            <li
              v-for="customer in recentCustomers"
              :key="customer.id"
              class="overview-list-item"
            >
              <div class="d-flex justify-content-between">
                <strong>{{ customer.name || '—' }}</strong>
                <span class="text-nowrap text-muted small ml-1">
                  {{ formatDateTime(customer.created_at) }}
                </span>
              </div>
              <div class="small text-muted">
                {{ customer.email }}
                <span v-if="customer.phone"> · {{ customer.phone }}</span>
              </div>
            </li>
          </ul>

          <router-link
            :to="{ name: 'customers' }"
            class="font-weight-bold"
          >
            View customers
          </router-link>
        </b-card>
      </b-col>

      <!-- Internal messages -->
      <b-col
        cols="12"
        md="6"
        class="mb-2"
      >
        <b-card class="overview-card h-100">
          <div class="d-flex justify-content-between align-items-start mb-1">
            <div class="d-flex align-items-start">
              <b-avatar
                size="42"
                rounded
                variant="light-primary"
                class="mr-1 flex-shrink-0"
              >
                <feather-icon
                  icon="MessageSquareIcon"
                  size="18"
                />
              </b-avatar>
              <div>
                <h4 class="mb-25">
                  Internal messages
                </h4>
                <p class="text-muted mb-0 small">
                  System messages, notifications and messages between your team
                </p>
              </div>
            </div>
            <b-badge
              variant="secondary"
              class="overview-count"
            >
              {{ internalMessagesCount }}
            </b-badge>
          </div>

          <p class="mb-0 small text-muted">
            There are no messages still
          </p>
        </b-card>
      </b-col>

      <!-- Barcode scanner -->
      <b-col
        cols="12"
        md="6"
        class="mb-2"
      >
        <b-card class="overview-card h-100">
          <div class="d-flex justify-content-between align-items-start mb-1">
            <div class="d-flex align-items-start">
              <b-avatar
                size="42"
                rounded
                :variant="scannerDetected ? 'light-success' : 'light-secondary'"
                class="mr-1 flex-shrink-0"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M3 5v14" />
                  <path d="M8 5v14" />
                  <path d="M12 5v14" />
                  <path d="M17 5v14" />
                  <path d="M21 5v14" />
                </svg>
              </b-avatar>
              <div>
                <h4 class="mb-25">
                  Barcode scanner
                </h4>
                <p class="text-muted mb-0 small">
                  USB / Bluetooth keyboard-wedge scanners
                </p>
              </div>
            </div>
            <b-badge
              :variant="scannerDetected ? 'success' : 'secondary'"
              class="overview-count"
            >
              {{ scannerDetected ? 'Connected' : 'Not detected' }}
            </b-badge>
          </div>

          <p
            v-if="!scannerDetected"
            class="text-muted small mb-50"
          >
            Scan any barcode on this page to confirm your scanner is working.
          </p>

          <b-form-group
            class="mb-1"
            label-for="overview-scanner-test"
          >
            <BarcodeTargetField hint-key="admin-overview-scanner-test">
              <b-form-input
                id="overview-scanner-test"
                v-model="scannerTestCode"
                class="barcode-target"
                type="text"
                size="sm"
                placeholder="Scanned barcode will appear here"
                autocomplete="off"
              />
            </BarcodeTargetField>
          </b-form-group>

          <p class="mb-50 small">
            Use your scanner on fields marked with the barcode icon when creating
            {{ ordersLabel.toLowerCase() }}, searching products, or updating stock.
          </p>
          <p class="mb-0 small text-muted">
            Focus is not always required — scans are routed to the active barcode field automatically.
          </p>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BAlert, BAvatar, BBadge, BCard, BCol, BFormGroup, BFormInput, BRow, BSpinner,
} from 'bootstrap-vue'
import axios from '@/libs/axios'
import { mapGetters } from 'vuex'
import { SALE_STATUSES } from '@/constants/enums'
import { isStaffMember } from '@/libs/staffPermissions'
import {
  hasDetectedBarcodeScanner,
  onBarcodeScanDetected,
} from '@/libs/barcodeScanner'
import BarcodeTargetField from '@/views/Components/Elements/BarcodeTargetField.vue'

export default {
  name: 'Admin',
  components: {
    BAlert,
    BAvatar,
    BBadge,
    BCard,
    BCol,
    BFormGroup,
    BFormInput,
    BRow,
    BSpinner,
    BarcodeTargetField,
  },
  data() {
    return {
      loading: true,
      loadError: null,
      view: 'owner',
      orders: {
        new_count: 0,
        items: [],
      },
      waitingStaff: {
        has_staff: false,
        items: [],
      },
      lowStockProducts: [],
      recentCustomers: [],
      scannerDetected: hasDetectedBarcodeScanner(),
      scannerTestCode: '',
      unsubscribeScanner: null,
      internalMessagesCount: 0,
    }
  },
  computed: {
    ...mapGetters({
      user: 'auth/user',
    }),
    userDisplayName() {
      if (!this.user) {
        return ''
      }
      return this.user.name || this.user.email || ''
    },
    isOwnerView() {
      return this.view === 'owner' && !isStaffMember(this.user)
    },
    companyPurpose() {
      return window.companyPurpose
    },
    ordersLabel() {
      return this.companyPurpose === 'rent' ? 'Bookings' : 'Orders'
    },
    ordersRouteName() {
      return this.companyPurpose === 'rent' ? 'bookings' : 'orders'
    },
    productsLabel() {
      return this.companyPurpose === 'rent' ? 'Units' : 'Products'
    },
    productsRouteName() {
      return this.companyPurpose === 'rent' ? 'units' : 'products'
    },
    ordersTitle() {
      return this.isOwnerView ? this.ordersLabel : `New ${this.ordersLabel.toLowerCase()}`
    },
    ordersSubtitle() {
      if (this.isOwnerView) {
        return `New ${this.ordersLabel.toLowerCase()} and activity from the last 24 hours`
      }
      return `${this.ordersLabel} without a supervisor, waiting for someone to take action`
    },
    ordersIcon() {
      return this.companyPurpose === 'rent' ? 'CalendarIcon' : 'ShoppingCartIcon'
    },
  },
  created() {
    this.fetchOverview()
    this.unsubscribeScanner = onBarcodeScanDetected(() => {
      this.scannerDetected = true
    })
  },
  beforeDestroy() {
    if (this.unsubscribeScanner) {
      this.unsubscribeScanner()
      this.unsubscribeScanner = null
    }
  },
  methods: {
    async fetchOverview() {
      this.loading = true
      this.loadError = null
      try {
        const { data } = await axios.get('api/admin/overview')
        const payload = data?.data || {}
        this.view = payload.view || (isStaffMember(this.user) ? 'staff' : 'owner')
        this.orders = payload.orders || { new_count: 0, items: [] }
        this.waitingStaff = payload.waiting_staff || { has_staff: false, items: [] }
        this.lowStockProducts = payload.low_stock_products || []
        this.recentCustomers = payload.recent_customers || []
      } catch (error) {
        this.loadError = error?.response?.data?.message || 'Could not load dashboard overview.'
      } finally {
        this.loading = false
      }
    },
    statusName(statusId) {
      const status = SALE_STATUSES.find(s => s.id === Number(statusId))
      return status?.name || 'Unknown'
    },
    formatDateTime(value) {
      if (!value) {
        return ''
      }
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return ''
      }
      return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatMoney(value) {
      const amount = Number(value)
      if (Number.isNaN(amount)) {
        return ''
      }
      return amount.toLocaleString(undefined, {
        style: 'currency',
        currency: 'CAD',
        minimumFractionDigits: 2,
      })
    },
  },
}
</script>

<style scoped>
.overview-count {
  font-size: 1.1rem;
  min-width: 2rem;
  padding: 0.4rem 0.65rem;
}
.overview-list-item {
  padding: 0.45rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.overview-list-item:last-child {
  border-bottom: 0;
}
.overview-card h4 {
  font-size: 1.15rem;
}
</style>
